import React, { useEffect, useState } from 'react';
import {
  TextInput, SelectInput, BooleanInput, DateInput, NumberInput, required, useTranslate,
} from 'react-admin';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { ACCOUNT_STATUSES, LOGGING_STATUSES } from '@/config/constants';
import { FieldPair, FormSection } from '@/components/shell';
import { httpClient } from '@/utils/httpClient';
import { useServerChoices } from '@/features/servers/useServerChoices';

interface LicenseOption {
  id: string | number;
  licenseName: string;
}

/*
 * The create payload is flat, while the edit page edits the record as the API returns it (nested
 * under accountResponse / licenseResponse), so each field names its source for both.
 */
const SOURCES = {
  accountName: ['accountName', 'accountResponse.accountName'],
  accountStatus: ['accountStatus', 'accountResponse.accountStatus'],
  licenseId: ['licenseId', 'licenseResponse.id'],
  primaryServerId: ['primaryServerId', 'accountResponse.primaryServer.id'],
  secondaryServerId: ['secondaryServerId', 'accountResponse.secondaryServer.id'],
  rateLimitApplies: ['rateLimitApplies', 'accountResponse.rateLimitApplies'],
  rateLimitPerMin: ['rateLimitPerMin', 'accountResponse.rateLimitPerMin'],
  rateLimitPerDay: ['rateLimitPerDay', 'accountResponse.rateLimitPerDay'],
  loggingStatus: ['loggingStatus', 'accountResponse.loggingStatus'],
  allowDebugOutput: ['allowDebugOutput', 'accountResponse.allowDebugOutput'],
} as const;

/**
 * The fields of an account, grouped by what the operator is deciding, shared by the create and
 * edit pages. The API key is set at creation only; afterwards it changes from the list's row menu.
 */
export const AccountFields: React.FC<{ mode: 'create' | 'edit' }> = ({ mode }) => {
  const t = useTranslate();
  const theme = useTheme();
  const { choices: serverChoices, loading: serversLoading } = useServerChoices();
  const [licenses, setLicenses] = useState<LicenseOption[]>([]);
  const [licensesLoading, setLicensesLoading] = useState(true);

  useEffect(() => {
    httpClient
      .get<LicenseOption[]>('/admin/license/GetAllLicense')
      .then((r) => setLicenses(r.data))
      .finally(() => setLicensesLoading(false));
  }, []);

  if (serversLoading || licensesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const source = (key: keyof typeof SOURCES) => SOURCES[key][mode === 'create' ? 0 : 1];
  // The edit record carries the license id as a string; the create payload sends it as the API lists it.
  const licenseChoices = licenses.map((l) => ({ id: mode === 'edit' ? String(l.id) : l.id, name: l.licenseName }));
  const statusChoices = ACCOUNT_STATUSES.map((c) => ({ id: c.id, name: t(`screens.accounts.status.${c.id}`) }));
  const loggingChoices = LOGGING_STATUSES.map((c) => ({ id: c.id, name: t(`screens.accounts.logging.${c.id}`) }));

  return (
    <>
      <FormSection title={t('screens.accounts.sectionAccount')}>
        <FieldPair>
          <TextInput
            source={source('accountName')}
            label={t('resources.accounts.fields.accountResponse.accountName')}
            validate={required()}
            fullWidth
          />
          <SelectInput
            source={source('accountStatus')}
            label={t('resources.accounts.fields.accountResponse.accountStatus')}
            choices={statusChoices}
            translateChoice={false}
            validate={required()}
            fullWidth
          />
        </FieldPair>
      </FormSection>

      {mode === 'create' && (
        <FormSection title={t('screens.accounts.sectionAccess')}>
          <TextInput
            source="apiKey"
            label={t('screens.accounts.apiKey')}
            validate={required()}
            fullWidth
            InputProps={{ sx: { fontFamily: theme.custom.text.mono } }}
          />
        </FormSection>
      )}

      <FormSection title={t('screens.accounts.sectionLicense')}>
        <FieldPair>
          <SelectInput
            source={source('licenseId')}
            label={t('resources.accounts.fields.licenseResponse.licenseName')}
            choices={licenseChoices}
            translateChoice={false}
            validate={required()}
            fullWidth
          />
          <DateInput
            source="validityTimeFrame"
            label={t('resources.accounts.fields.validityTimeFrame')}
            validate={required()}
            fullWidth
          />
        </FieldPair>
        <BooleanInput source="isLicenseBlocked" label={t('screens.accounts.licenseBlocked')} />
      </FormSection>

      <FormSection title={t('screens.accounts.sectionServers')}>
        <FieldPair>
          <SelectInput
            source={source('primaryServerId')}
            label={t('resources.accounts.fields.primaryServer')}
            choices={serverChoices}
            translateChoice={false}
            validate={required()}
            fullWidth
          />
          <SelectInput
            source={source('secondaryServerId')}
            label={t('resources.accounts.fields.secondaryServer')}
            choices={serverChoices}
            translateChoice={false}
            fullWidth
          />
        </FieldPair>
      </FormSection>

      <FormSection title={t('screens.accounts.sectionLimits')}>
        <BooleanInput source={source('rateLimitApplies')} label={t('screens.accounts.rateLimitApplies')} />
        <FieldPair>
          <NumberInput source={source('rateLimitPerMin')} label={t('screens.accounts.perMin')} fullWidth />
          <NumberInput source={source('rateLimitPerDay')} label={t('screens.accounts.perDay')} fullWidth />
        </FieldPair>
      </FormSection>

      <FormSection title={t('screens.accounts.sectionLogging')}>
        <SelectInput
          source={source('loggingStatus')}
          label={t('screens.accounts.loggingLabel')}
          choices={loggingChoices}
          translateChoice={false}
          validate={required()}
          fullWidth
        />
        <BooleanInput
          source={source('allowDebugOutput')}
          label={t('resources.accounts.fields.accountResponse.allowDebugOutput')}
          helperText={t('resources.accounts.fields.accountResponse.allowDebugOutputHelp')}
        />
      </FormSection>
    </>
  );
};
